import { useWeb3React } from '@web3-react/core'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdaterChainId } from '~/hooks'
import { useLimitOrderRouter } from '~/hooks/useLimitOrderRouter'
import TokenLogo from '~/components/TokenLogo'
import { useClickAway } from '~/hooks'
import { getExplorerTokenLink } from '~/utils/chain'
import { LOCALES } from '~/locales'
import {
  ModalContainer,
  ModalContent,
  ModalButton,
  IconClose,
  TextWarningTitle,
  IconWarning,
  IconBlocked,
  TextWarning,
  WaringToken,
  TextSymbol,
  Typography,
  Divider,
  IconNav,
  DegenTag,
  StyledTokenExplorerWrapper,
  StyledConfirmArea,
  IconChecked,
  IconUnchecked,
} from './styled'
import { Row, RowBetween } from '~/components/Layout'
import { TokenSelectWarningProps } from './types'
import { useTokenBalance } from '~/state/wallet/hooks'
import { useNetworkValidator } from '~/hooks'

const TokenSelectWarning: React.FC<TokenSelectWarningProps> = ({
  visible: visibleProps,
  onClose,
  onSelect,
  token,
  blocked,
}) => {
  const { t, i18n } = useTranslation()
  const expectChainId = useUpdaterChainId()
  const isLimitOrderRouter = useLimitOrderRouter()
  const { isArbitrum } = useNetworkValidator()

  const ref = useRef()
  const [visible, setVisible] = useState(
    typeof visibleProps !== 'undefined' ? visibleProps : true,
  )
  const [checked, setChecked] = useState<boolean>(false)
  const { account } = useWeb3React()
  const balance = useTokenBalance(account, token)

  useClickAway(ref, () => {
    setVisible(false)
  })

  useEffect(() => {
    setVisible(visibleProps)
  }, [visibleProps])

  if (!visible || !token) return null

  const handleClose = () => {
    typeof visibleProps !== 'undefined' && setVisible(false)
    onClose && onClose()
  }

  const handleChecked = () => {
    setChecked(!checked)
  }

  const onViewContractDetail = () => {
    window.open(
      getExplorerTokenLink(
        token.address,
        expectChainId,
        i18n.resolvedLanguage as LOCALES,
      ),
      '_blank',
      'noopener,noreferrer',
    )
  }

  return (
    <ModalContainer>
      <ModalContent>
        <IconClose onClick={handleClose} position="left" />
        {blocked ? <IconBlocked /> : <IconWarning />}
        {!blocked && <TextWarningTitle>{t('risk_reminder')}</TextWarningTitle>}
        <TextWarning>
          {blocked ? t('token_select_blocked') : t('token_select_warning')}
        </TextWarning>
        <WaringToken>
          <Row>
            <TokenLogo
              logo={Reflect.get(token, 'logo')}
              symbol={token.symbol}
            />
            <TextSymbol fontSize={17}>{token.symbol}</TextSymbol>
            {balance ? (
              <Typography
                fontSize={17}
                fontWeight={500}
                style={{ marginLeft: 'auto', marginBottom: 0 }}
              >
                {balance.toDecimalsFixed()}
              </Typography>
            ) : null}
            {blocked ? <DegenTag>{t('unknown_source')}</DegenTag> : null}
          </Row>
          <Divider spacing={12} />
          <StyledTokenExplorerWrapper onClick={onViewContractDetail}>
            <RowBetween>
              <Typography
                fontSize={13}
                lineHeight={17}
                style={{ marginBottom: 0 }}
              >
                {t(
                  isLimitOrderRouter && isArbitrum
                    ? 'view_on_Arbiscan'
                    : 'view_on_Etherscan',
                )}
              </Typography>
              <IconNav />
            </RowBetween>
            <Typography
              fontSize={12}
              lineHeight={17}
              color="#6F7390"
              style={{ marginTop: '8px', marginBottom: 0 }}
            >
              {token.address}
            </Typography>
          </StyledTokenExplorerWrapper>
        </WaringToken>
        <StyledConfirmArea>
          {checked ? (
            <IconChecked onClick={handleChecked} />
          ) : (
            <IconUnchecked onClick={handleChecked} />
          )}
          <Typography
            fontSize={12}
            lineHeight={19}
            color="#9598AB"
            style={{ marginBottom: 0 }}
          >
            {t('i_understand')}
          </Typography>
        </StyledConfirmArea>
        <ModalButton
          disabled={!checked}
          onClick={() => (blocked ? onClose() : onSelect(token))}
          className={!checked && 'disabled'}
        >
          {t(blocked ? 'back' : 'import_token')}
        </ModalButton>
      </ModalContent>
    </ModalContainer>
  )
}

export default TokenSelectWarning
